/**
 * Nutrition (F5): nutrient registry ("+add nutrient"), actual intake entry
 * (`/v2/stats/intake`), and daily balance (`/v2/stats/nutrition/daily` —
 * all nutrients + caffeine + water). All routes are SELF-ONLY; the
 * trainer's view of a client's balance goes through the F6 read layer. The
 * default window (without from/to) is computed by the BE in the user's
 * timezone.
 */
import type { ApiCreateNutrientDto, ApiCreateIntakeDto } from '@mfw/common';

import type {
  FetchNutritionDailyArgs,
  DeleteIntakeResult,
  DeleteIntakeArgs,
  ApiNutritionDay,
  FetchIntakeArgs,
  ApiIntakeEntry,
  ApiNutrient,
} from './types/statsInsights.types';

import { destroy, post, get } from './axios';

export const fetchNutrients = async () => {
  const response = await get<ApiNutrient[]>('/v2/stats/nutrients');
  return response ?? null;
};

export const createNutrient = async (dto: ApiCreateNutrientDto) => {
  const response = await post<ApiNutrient>('/v2/stats/nutrients', dto);
  // BE returns a raw row, but the `post` helper types a `{data}` wrapper — cast
  return (response ?? null) as ApiNutrient | null;
};

export const createIntake = async (dto: ApiCreateIntakeDto) => {
  const response = await post<ApiIntakeEntry>('/v2/stats/intake', dto);
  // BE returns a raw row, but the `post` helper types a `{data}` wrapper — cast
  return (response ?? null) as ApiIntakeEntry | null;
};

export const fetchIntake = async (args: FetchIntakeArgs = {}) => {
  const response = await get<ApiIntakeEntry[]>('/v2/stats/intake', {
    query: { from: args.from, to: args.to },
  });
  return response ?? null;
};

export const deleteIntake = async ({ id }: DeleteIntakeArgs) => {
  // the `destroy` helper doesn't return a body (BE sends `{ deleted: true }`)
  // — success without an exception means it was deleted
  await destroy(`/v2/stats/intake/${id}`);
  return { deleted: true } as DeleteIntakeResult;
};

export const fetchNutritionDaily = async (
  args: FetchNutritionDailyArgs = {},
) => {
  const response = await get<ApiNutritionDay[]>('/v2/stats/nutrition/daily', {
    query: { from: args.from, to: args.to },
  });
  return response ?? null;
};
